import React, { useState } from "react";
import { TicketPercent, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtMoney } from "@/lib/razorpay";

export default function CouponField({ country, applied, onChange }) {
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");

  const apply = async () => {
    if (!code.trim()) return;
    setChecking(true);
    setError("");
    try {
      const { data } = await api.post("/payments/coupons/validate", { code: code.trim(), country });
      onChange(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not apply coupon");
    } finally {
      setChecking(false);
    }
  };

  if (applied) {
    const off = applied.discount_type === "percent" ? `${applied.value}% off` : `${fmtMoney(applied.value * 100, "INR")} off`;
    return (
      <div data-testid="coupon-applied" className="flex items-center justify-between gap-3 p-3 rounded-2xl bg-leaf/10 border-2 border-leaf/40">
        <div className="text-sm">
          <div className="font-bold text-leaf flex items-center gap-1.5"><TicketPercent className="w-4 h-4" /> {applied.code} applied · {off}</div>
          <div className="text-ink font-semibold">
            <span className="line-through text-stone mr-2">{fmtMoney(applied.original_amount, applied.currency)}</span>
            <span data-testid="coupon-discounted-amount">{fmtMoney(applied.amount, applied.currency)}</span>/mo
          </div>
        </div>
        <button type="button" data-testid="coupon-remove" onClick={() => { onChange(null); setCode(""); }} className="p-2 rounded-full hover:bg-white text-stone" title="Remove coupon">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-bold text-ink mb-2">Coupon code <span className="text-stone font-normal">(optional)</span></label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <TicketPercent className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pebble" />
          <input
            data-testid="coupon-input"
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
            placeholder="e.g. DIWALI20"
            className="w-full h-12 pl-12 pr-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-medium bg-white uppercase"
          />
        </div>
        <button type="button" data-testid="coupon-apply" onClick={apply} disabled={checking || !code.trim()} className="px-5 h-12 rounded-2xl bg-ink text-white font-bold text-sm disabled:opacity-50">
          {checking ? "…" : "Apply"}
        </button>
      </div>
      {error && <p data-testid="coupon-error" className="text-xs text-watermelon font-semibold mt-1">{error}</p>}
    </div>
  );
}
